import { Button } from "@indigo-components";
import type { notification } from "antd";
import { useEffect } from "react";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { useSessionExpiry } from "~/hooks/useSessionExpiry";
import { AuthUserGroup } from "~/types/auth";

type NotificationInstance = ReturnType<typeof notification.useNotification>[0];

/** Shared key so repeat opens replace the toast instead of stacking. */
const NOTIFICATION_KEY = "session-expiry";

type Phase = "warning" | "expired";

/**
 * Proactive counterpart to SessionExpiredBanner: warns *before* the Cognito
 * refresh token dies, while the user can still finish and save their work.
 *
 * Two phases, because the danger doesn't end at the deadline:
 * - `warning` — inside the last hour of the refresh-token window.
 * - `expired` — past the deadline, but the app still appears to work. ID tokens
 *   outlive that moment, so the first failed refresh can land minutes or hours
 *   later, mid-task. This is the phase that produced silent data loss in the
 *   wild; the profile-avatar tooltip goes *quiet* here because it only tracks
 *   `isWarning`.
 *
 * Opens on phase changes only, so closing the toast keeps it closed — but an
 * escalation from `warning` to `expired` re-opens it, and so does signing in
 * again (a fresh `auth_time` that later ages back into `warning`).
 */
/**
 * Live countdown text for the `warning` toast.
 *
 * Rendered *inside* the notification (antd renders `description` through the
 * `contextHolder` that sits in the React tree), so it subscribes to the clock
 * itself and re-renders every tick while it stays mounted. Baking the label
 * into a string at `api.open()` time instead would freeze it at whatever it
 * said when the toast opened — up to an hour stale, still cheerfully claiming
 * "59 minutes" a minute before expiry, which is the exact false reassurance
 * this notification exists to remove.
 */
const WarningDescription = () => {
  const { remainingLabel } = useSessionExpiry();
  return (
    <>
      You&apos;ll be signed out in {remainingLabel ?? "less than a minute"}.
      Sign in again to keep saving your work — you can sign in on a new tab and
      come back to this one.
    </>
  );
};

export function useSessionExpiryNotification(api: NotificationInstance): void {
  const { sessionExpired, userGroup } = useAuthSession();
  const { isWarning, isExpired } = useSessionExpiry();

  // Internal staff only, matching the profile-warning tooltip's audience.
  // Suppressed once grace mode starts — SessionExpiredBanner owns the message
  // from that point, and two things saying "signed out" is worse than one.
  const suppressed =
    sessionExpired || userGroup === null || userGroup === AuthUserGroup.DOCTOR;

  const phase: Phase | null = suppressed
    ? null
    : isExpired
      ? "expired"
      : isWarning
        ? "warning"
        : null;

  useEffect(() => {
    if (phase === null) {
      // Recovered, or the banner has taken over.
      api.destroy(NOTIFICATION_KEY);
      return;
    }

    const expired = phase === "expired";
    api.open({
      className: "font-diatype",
      key: NOTIFICATION_KEY,
      message: expired ? "Session expired" : "Session expiring soon",
      description: expired ? (
        "Sign in again to keep saving your work. You can sign in on a new tab and come back to this one."
      ) : (
        <WarningDescription />
      ),
      duration: 0, // stay open until the user closes it
      actions: (
        <Button
          variant="primary"
          size="sm"
          onClick={() => window.open("/login", "_blank", "noopener")}
        >
          Sign in
        </Button>
      ),
      placement: "bottomLeft",
    });
  }, [api, phase]);
}
